import { type BufferGeometry, Group, type Material, Mesh, MeshStandardMaterial } from "three";

import { CANNON_TURRET, KINETIC_TURRET, type StructureKind, TURRET } from "src/shared/industry";
import { armyColor, PALETTE } from "./palette";

/** The shapes a building is drawn from — `IndustryViews`' own pools. */
export interface StructureParts {
  pad: BufferGeometry;
  padHeight: number;
  crate: BufferGeometry;
  crateScale: { x: number; y: number; z: number };
  turret?: { geometry: BufferGeometry; material: Material };
}

/**
 * A building in the hand — what a build or a token being dragged off the
 * hotbar looks like before it is stood anywhere.
 *
 * **Real meshes, not a slot in the pools**: those are instanced and drawn from
 * the board's own list of structures, and this is not on it. The geometry is
 * shared and the materials are this object's own, so `dispose` is what it owes.
 */
export function buildStructureGhost(
  parts: StructureParts,
  kind: StructureKind,
  owner: number
): { object: Group; dispose(): void } {
  const object = new Group();
  const owned: Material[] = [];

  if ((kind === TURRET || kind === KINETIC_TURRET || kind === CANNON_TURRET) && parts.turret) {
    const gun = new Mesh(parts.turret.geometry, parts.turret.material);
    gun.position.y = parts.padHeight;
    gun.castShadow = true;
    object.add(gun);
  } else {
    const padMaterial = new MeshStandardMaterial({
      color: armyColor(owner),
      roughness: 0.85,
      metalness: 0.05,
      flatShading: true
    });
    const crateMaterial = new MeshStandardMaterial({
      color: PALETTE.store,
      roughness: 0.7,
      metalness: 0.1,
      flatShading: true
    });
    owned.push(padMaterial, crateMaterial);

    const pad = new Mesh(parts.pad, padMaterial);
    pad.position.y = parts.padHeight / 2;
    pad.castShadow = true;
    object.add(pad);

    const crate = new Mesh(parts.crate, crateMaterial);
    crate.position.y = parts.padHeight;
    crate.scale.set(parts.crateScale.x, parts.crateScale.y, parts.crateScale.z);
    crate.castShadow = true;
    object.add(crate);
  }

  return { object, dispose: () => owned.forEach((material) => material.dispose()) };
}
